import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatMoeda } from '@fiodecobre/shared';
import { useAlertaFaixa } from './alertas/AlertasContexto';

interface Resumo {
  qtd_vencido: number;
  total_vencido: string;
  qtd_hoje: number;
  total_hoje: string;
}

const POLLING_MS = 5 * 60 * 1000;
const KEY_DISMISS = 'contas_pagar.alerta.dismiss';

function dismissedHoje(): boolean {
  const v = localStorage.getItem(KEY_DISMISS);
  if (!v) return false;
  return v === new Date().toISOString().slice(0, 10);
}

export function AlertaContasPagarBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [dismissed, setDismissed] = useState(dismissedHoje);

  const podeVer = usuario && ['admin', 'gerente', 'comprador'].includes(usuario.perfil);

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await api.get<Resumo>('/api/contas-pagar/alertas-resumo');
        if (!cancelado) setResumo(r);
      } catch {}
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer]);

  const ativo = !!(podeVer && !dismissed && resumo && (resumo.qtd_vencido > 0 || resumo.qtd_hoje > 0));
  const temVencido = !!resumo && resumo.qtd_vencido > 0;
  const fundo = temVencido ? '#ef4444' : '#f97316';
  const borda = temVencido ? '#b91c1c' : '#c2410c';
  const texto = '#000';

  const recolhido = useAlertaFaixa(
    'contas-pagar',
    ativo
      ? { rotulo: 'Contas a pagar', bg: fundo, borda, qtd: resumo!.qtd_vencido + resumo!.qtd_hoje }
      : null,
  );

  if (!ativo || recolhido || !resumo) return null;

  function fechar() {
    localStorage.setItem(KEY_DISMISS, new Date().toISOString().slice(0, 10));
    setDismissed(true);
  }

  return (
    <div
      style={{
        background: fundo,
        borderLeft: `4px solid ${borda}`,
        color: texto,
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 13,
        cursor: 'pointer',
      }}
      onClick={() => navigate('/contas-pagar')}
    >
      {temVencido ? <AlertTriangle size={16} /> : <Calendar size={16} />}
      <span style={{ fontWeight: 600 }}>Contas a pagar:</span>
      {temVencido && (
        <span>
          {resumo.qtd_vencido} vencida{resumo.qtd_vencido === 1 ? '' : 's'} ({formatMoeda(resumo.total_vencido)})
        </span>
      )}
      {temVencido && resumo.qtd_hoje > 0 && <span>·</span>}
      {resumo.qtd_hoje > 0 && (
        <span>
          {resumo.qtd_hoje} vence{resumo.qtd_hoje === 1 ? '' : 'm'} hoje ({formatMoeda(resumo.total_hoje)})
        </span>
      )}
      <span style={{ marginLeft: 'auto', textDecoration: 'underline' }}>Ver lista</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); fechar(); }}
        title="Dispensar ate amanha"
        style={{ background: 'transparent', border: 'none', color: texto, cursor: 'pointer', padding: 2 }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
